import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';

import ElementHeightObserver from 'components/ElementHeightObserver';
import { BroadCastChannels, BroadCastHandledTypes, isTabletOrMobileDevice } from 'constants/app';
import { marketGraphsBranding } from 'constants/branding';
import { MARKET_GRAPH_BASE_URL } from 'constants/locations';
import { ChartSelections, MARKET_GRAPH_ODDS_FILTER_LOCAL_STORAGE_NAME } from 'constants/marketGraph';
import { getIsMarketGraphsDarkThemeEnabled } from 'redux/modules/appConfigs/selectors';
import { getLoggedInStatusState } from 'redux/modules/auth/selectors';
import {
  getIsMarketDefinitionMarketClosed,
  getMarketDefinitionIsMarketDisabled
} from 'redux/modules/marketDefinitions/selectors';
import { setCurrentSelection } from 'redux/modules/marketGraph';
import { getMarketGraphIsMarketError } from 'redux/modules/marketGraph/selectors';

import MarketGraphBlacklistedMarket from './components/MarketGraphBlacklistedMarket/MarketGraphBlacklistedMarket';
import MarketGraphContent from './components/MarketGraphContent/MarketGraphContent';
import MarketGraphHeader from './components/MarketGraphHeader/MarketGraphHeader';

import styles from './MarketGraphPage.module.scss';

export default function MarketGraphPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { marketId = '', selectionId, handicap = 0 } = useParams();
  const navigate = useNavigate();

  const isLoggedIn = useSelector(getLoggedInStatusState);
  const isDarkTheme = useSelector(getIsMarketGraphsDarkThemeEnabled);
  const isMarketDisabled = useSelector(getMarketDefinitionIsMarketDisabled(marketId));
  const isMarketError = useSelector(getMarketGraphIsMarketError);
  const isMarketClosed = useSelector(getIsMarketDefinitionMarketClosed(marketId));

  useEffect(() => {
    if (marketId && selectionId) {
      dispatch(setCurrentSelection({ marketId, selectionId: +selectionId, handicap: +handicap }));
    }
    return () => {
      dispatch(setCurrentSelection(null));
    };
  }, [marketId, selectionId]);

  useEffect(() => {
    if (!isLoggedIn) {
      const oddsFilter = localStorage.getItem(MARKET_GRAPH_ODDS_FILTER_LOCAL_STORAGE_NAME);

      if (oddsFilter) {
        localStorage.removeItem(MARKET_GRAPH_ODDS_FILTER_LOCAL_STORAGE_NAME);
      }
    }

    if (!isTabletOrMobileDevice) {
      const channel = new BroadcastChannel(BroadCastChannels.DESKTOP_MARKET_GRAPH_PAGE);

      channel.postMessage({
        type: BroadCastHandledTypes.DESKTOP_MARKET_GRAPH_OPENED,
        windowName: window.name,
        marketId,
        selectionId,
        handicap
      });

      channel.onmessage = event => {
        if (
          event.data.type === BroadCastHandledTypes.DESKTOP_MARKET_GRAPH_WINDOW_NEW_PARAMETERS &&
          event.data.windowName === window.name
        ) {
          navigate(
            `${MARKET_GRAPH_BASE_URL}/${event.data.marketId}/${event.data.selectionId}/${
              event.data.handicap
            }?selection=${event.data.selection ?? ChartSelections.odds}`,
            {
              replace: true
            }
          );
        }
      };

      const handleCloseWindow = () => {
        channel.postMessage({ type: BroadCastHandledTypes.DESKTOP_MARKET_GRAPH_CLOSED, windowName: window.name });
        channel.close();
      };

      window.addEventListener('beforeunload', handleCloseWindow);

      (window as any).loaded = true; // required to stop interval in MarketGraphButton

      return () => {
        window.removeEventListener('beforeunload', handleCloseWindow);
      };
    }
  }, []);

  if (!isLoggedIn) {
    return (
      <ElementHeightObserver>
        <Helmet>
          <title>
            {t('general.page.title')} | {t('marketGraph.labels.title')}
          </title>
        </Helmet>
        <div className={classNames(styles.content, { [marketGraphsBranding.DARK_THEME]: isDarkTheme })}>
          <MarketGraphHeader />
          <div
            className={classNames(styles.unauthorized__wrapper, {
              [styles.unauthorized__wrapper__mobile]: isTabletOrMobileDevice
            })}
          >
            <div className={styles.unauthorized__container}>
              <i className={classNames('biab_custom-icon-login', styles.unauthorized__icon)} />
              <p className={styles.unauthorized__text}>{t('marketGraph.labels.pleaseLogin')}</p>
            </div>
          </div>
        </div>
      </ElementHeightObserver>
    );
  }
  // There is 2 cases when 'enabled' field in market definitions is false:
  // 1. Market was blacklisted (will be handled as expected)
  // 2. Market was closed and after about 1 minute BE respond with 'enabled: false' in market definitions
  // For 2nd case we need to make priority for CLOSED status of market (in market definitions), that means that we have
  // to show MarketGraphBlacklistedMarket only in case when market wasn't closed (it's active), otherwise (if market was closed)
  // we handle it like closed and show 'Market is closed' label in MarketGraphContent
  if (isMarketError || (isMarketDisabled && !isMarketClosed)) {
    return (
      <ElementHeightObserver>
        <MarketGraphBlacklistedMarket />
      </ElementHeightObserver>
    );
  }

  return (
    <ElementHeightObserver>
      <Helmet>
        <title>
          {t('general.page.title')} | {t('marketGraph.labels.title')}
        </title>
      </Helmet>
      <div className={classNames(styles.content, { [marketGraphsBranding.DARK_THEME]: isDarkTheme })}>
        <MarketGraphHeader />
        <MarketGraphContent />
      </div>
    </ElementHeightObserver>
  );
}
